/**
 * SOLOMON — LEAD CONSTANTS
 * Shared between LeadsList, LeadDetail, sidebar nav, and any future
 * lead-related UI. Single source of truth for state labels, color classes,
 * and the tab grouping.
 */

// State pill labels — short and operator-readable.
export const STATE_LABELS = {
  new:               'New',
  contacted:         'Contacted',
  responded_warm:    'Warm',
  responded_unsure:  'Unsure',
  appointment_set:   'Appt',
  offered:           'Offered',
  under_contract:    'Contract',
  closed:            'Closed',
  dead:              'Dead',
  do_not_contact:    'DNC',
  opted_out:         'Opted Out',
  revived_inquiry:   'Revived',
};

// CSS class per state (color-coding within Hermès palette — see leads.css).
export const STATE_CLASS = {
  new:               'state-neutral',
  contacted:         'state-primary',
  responded_warm:    'state-warm',
  responded_unsure:  'state-soft',
  appointment_set:   'state-engaged',
  offered:           'state-prep',
  under_contract:    'state-success-strong',
  closed:            'state-success',
  dead:              'state-muted',
  do_not_contact:    'state-danger',
  opted_out:         'state-danger',
  revived_inquiry:   'state-attention',
};

// Pipeline tabs. `states: null` = all (no filter); otherwise filters by states.
// Single-state tabs round-trip through the server-side state filter; multi-state
// tabs fetch all and filter client-side (the API doesn't accept multi-state).
// Leads enter as "Non-contacted" (cold `new` imports + funnel `consented` opt-ins)
// and flow rightward through the tabs as their state changes.
export const STATE_TABS = [
  { id: 'non_contacted', label: 'Non-contacted', states: ['new', 'consented'] },
  { id: 'contacted',    label: 'Contacted',    states: ['contacted'] },
  { id: 'responded',    label: 'Responded',    states: ['responded_warm', 'responded_unsure'] },
  { id: 'appointment',  label: 'Appt',         states: ['appointment_set'] },
  { id: 'offered',      label: 'Offered',      states: ['offered'] },
  { id: 'contract',     label: 'Contract',     states: ['under_contract'] },
  { id: 'closed',       label: 'Closed',       states: ['closed'] },
  { id: 'inactive',     label: 'Inactive',     states: ['dead', 'do_not_contact', 'opted_out', 'revived_inquiry'] },
  { id: 'stale',        label: 'Stale (3d+)',  states: null, mode: 'stale' },
];

/**
 * Format an ISO timestamp into a relative human label.
 * Falls back to the locale date for older entries.
 */
export function formatRelativeTime(iso) {
  if (!iso) return '—';
  const then = new Date(iso);
  const now = new Date();
  const diffMs = now - then;
  if (diffMs < 0) return then.toLocaleDateString();
  const minutes = Math.floor(diffMs / 60000);
  if (minutes < 1)  return 'just now';
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24)   return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days < 7)     return `${days}d ago`;
  return then.toLocaleDateString();
}

/**
 * Map a numeric (string|number) into a USD currency label, or '—' for null.
 */
export function formatCurrency(v) {
  if (v == null || v === '') return '—';
  const n = typeof v === 'number' ? v : Number(String(v).replace(/[$,]/g, ''));
  if (!Number.isFinite(n)) return '—';
  return n.toLocaleString('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 });
}
